import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './LoginPage.css';
import HeaderImage from '../../assets/Images/Header.png';
import LottieLoader from '../LoadingSpinner/LottieLoader';

function LoginPage({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

    const getAxiosErrorMessage = (err) => {
      return (
        err?.response?.data?.message ||
        err?.response?.data?.code ||
        err?.message ||
        'Login failed'
      );
    };

    const shouldTryLegacyLogin = (wpErr) => {
      const status = wpErr?.response?.status;
      const data = wpErr?.response?.data || {};
      const code = String(data.code || '').toLowerCase();
      const message = String(data.message || '').toLowerCase();

      if (status === 401) return true;

      if (status === 403) {
        if (
          code.includes('incorrect_password') ||
          code.includes('invalid_username') ||
          code.includes('jwt_auth_invalid_token') ||
          message.includes('incorrect password') ||
          message.includes('invalid username') ||
          message.includes('invalid email')
        ) {
          return true;
        }
      }

      return false;
    };

    const handleLogin = async (e) => {
      e.preventDefault();

      if (isLoading) return;

      setIsLoading(true);
      setError('');
      localStorage.removeItem('currentLoginLogId');
    
    const typedIdentifier = email;

    try {
      // 1) WordPress JWT login (accepts email OR username)
      const jwtRes = await axios.post('https://edugroove.com/wp-json/jwt-auth/v1/token', {
        username: typedIdentifier,
        password,
      });
      const token = jwtRes.data.token;

      // 2) WP user info (get the canonical email + username)
      const userInfoRes = await axios.get(
        'https://edugroove.com/wp-json/wp/v2/users/me?context=edit',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const wpUser = userInfoRes.data;
      const roles = wpUser.roles || [];

      // Exchange the verified WordPress JWT for an EDU Groove backend token.
      const backendAuthRes = await axios.post(
        'https://edugroove-backend.onrender.com/api/auth/wordpress',
        { token }
      );
      const backendToken = backendAuthRes.data.token;
      localStorage.setItem('edugroove_access_token', backendToken);
      axios.defaults.headers.common.Authorization = `Bearer ${backendToken}`;

      const canonicalEmail = (wpUser.email || '').toLowerCase();
      const wpUsername = wpUser.slug || wpUser.username || wpUser.name || '';

      if (!canonicalEmail) {
        setError('Your WordPress account has no email. Please update your profile email in WordPress.');
        setIsLoading(false);
        return;
      }

      // 3) Ask WP for ARMember Group parent (use canonicalEmail)
      let parentEmailFromWP = '';
      try {
        const gpRes = await axios.get(
          'https://edugroove.com/wp-json/edugroove/v1/group-parent',
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { email: canonicalEmail },
          }
        );
        if (gpRes.data && gpRes.data.ok && gpRes.data.parent_email) {
          parentEmailFromWP = gpRes.data.parent_email;
        }
      } catch (e2) {
        console.warn('Could not fetch ARMember group parent:', e2?.response?.data || e2.message);
      }

      // 4) Role mapping (NO admin flag here)
      let subUserLimit = 0;
      let canManageSubUsers = false; // enterprise & homeschool can manage sub-users
      if (roles.includes('armember_enterprise') || roles.includes('armember_partnerschool')) {
        subUserLimit = 10;
        canManageSubUsers = true;
      } else if (roles.includes('armember_homeschool')) {
        subUserLimit = 0;
        canManageSubUsers = true;
      } else if (roles.includes('armember_solo')) {
        subUserLimit = 0;
        canManageSubUsers = false;
      } else if (roles.includes('armember_basic')) {
        subUserLimit = 0;
        canManageSubUsers = false;
      }
      const membership = roles.includes('armember_basic') ? 'free' : 'paid';

      // 5) Ensure user exists in app DB, always by canonicalEmail
      let appUser;
      try {
        const userRes = await axios.get(
          `https://edugroove-backend.onrender.com/api/users/by-email/${canonicalEmail}`
        );
        appUser = userRes.data;
      } catch (fetchErr) {
        // Create minimal user with canonicalEmail, then fetch
        try {
          await axios.post('https://edugroove-backend.onrender.com/api/users/create-from-wordpress', {
            email: canonicalEmail,
            password: generateRandomPassword(),
            // IMPORTANT: numeric, not boolean
            isAdmin: 0,
          });
        } catch (createErr) {
          if (!(createErr.response && createErr.response.status === 409)) {
            setError('Failed to create user in app database.');
            setIsLoading(false);
            return;
          }
        }
        const userRes2 = await axios.get(
          `https://edugroove-backend.onrender.com/api/users/by-email/${canonicalEmail}`
        );
        appUser = userRes2.data;
      }

      // 6) Link to parent (if WP says there is one and it isn't self)
      if (parentEmailFromWP && parentEmailFromWP.toLowerCase() !== canonicalEmail) {
        if (!appUser.parentId) {
          try {
            await axios.post('https://edugroove-backend.onrender.com/api/users/set-parent', {
              childEmail: canonicalEmail,
              parentEmail: parentEmailFromWP,
            });
            // refresh record to pull the new parentId
            const refreshed = await axios.get(
              `https://edugroove-backend.onrender.com/api/users/by-email/${canonicalEmail}`
            );
            appUser = refreshed.data;
          } catch (err) {
            console.warn('Failed to set parentId from WP group meta:', err?.response?.data || err.message);
          }
        }
      }

      // 7) Optional: membership expiry (your existing logic)
      if ((roles.includes('armember_solo') || roles.includes('armember_enterprise')) && !appUser.membershipExpiry) {
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
        try {
          await axios.post('https://edugroove-backend.onrender.com/api/users/update-expiry', {
            email: canonicalEmail,
            expiryDate: oneYearFromNow.toISOString().split('T')[0],
          });
          const refreshed2 = await axios.get(
            `https://edugroove-backend.onrender.com/api/users/by-email/${canonicalEmail}`
          );
          appUser = refreshed2.data;
        } catch (err) {
          console.warn('Could not set membership expiry:', err);
        }
      }

      // 8) Build client session
      // ONLY trust DB for admin: isAdmin === 1 numerically.
      const dbIsAdmin = Number(appUser?.isAdmin) === 1;

      const fullUser = {
        ...appUser,
        email: canonicalEmail,
        roles,
        subUserLimit,
        canManageSubUsers, // new flag for UI elsewhere
        isAdmin: dbIsAdmin, // <-- ONLY from DB
        wpUsername,
        planRole: roles.find(r => r.startsWith('armember_')) || 'armember_basic',
        membership,
        fromWordPress: true,
      };
      localStorage.setItem('edugroove_user', JSON.stringify(fullUser));
      localStorage.setItem('edugroove_login_saved_at', String(Date.now()));

      // 9) Log login
      try {
        const logRes = await axios.post('https://edugroove-backend.onrender.com/api/users/log-login', {
          userId: fullUser.id,
          email: canonicalEmail,
          parentId: fullUser.parentId || null,
        });

        if (logRes.data?.logId) {
          localStorage.setItem('currentLoginLogId', String(logRes.data.logId));
        }
      } catch (logErr) {
        console.warn('Failed to log login:', logErr);
      }

      onLoginSuccess(fullUser);
      setIsLoading(false);
      return;
        } catch (wpErr) {
          console.warn(
            'WordPress login failed:',
            wpErr?.response?.status,
            wpErr?.response?.data || wpErr.message
          );

          if (!shouldTryLegacyLogin(wpErr)) {
            setError('Login temporarily failed. Please try again in a moment.');
            setIsLoading(false);
            return;
          }
        }

    // 10) Fallback: backend /api/login (legacy sub-users / app-only users)
      try {
        console.log('Falling back to legacy app login for:', typedIdentifier);

        const subUserRes = await axios.post('https://edugroove-backend.onrender.com/api/login', {
          email: typedIdentifier,
          password,
        });

        const backendToken = subUserRes.data.token;
        localStorage.setItem('edugroove_access_token', backendToken);
        axios.defaults.headers.common.Authorization = `Bearer ${backendToken}`;

      try {
        const logRes = await axios.post('https://edugroove-backend.onrender.com/api/users/log-login', {
          userId: subUserRes.data.id,
          email: subUserRes.data.email,
          parentId: subUserRes.data.parentId || null,
        });

        if (logRes.data?.logId) {
          localStorage.setItem('currentLoginLogId', String(logRes.data.logId));
        }
      } catch (logErr) {
        console.warn('Failed to log login:', logErr);
      }

      const subUser = {
        ...subUserRes.data,
        isAdmin: Number(subUserRes.data.isAdmin) === 1,
        membership: subUserRes.data.membership || 'paid',
      };
      delete subUser.token;

      localStorage.setItem('edugroove_user', JSON.stringify(subUser));
      localStorage.setItem('edugroove_login_saved_at', String(Date.now()));
      onLoginSuccess(subUser);
    } catch (subUserErr) {
      console.error('Legacy login failed:', subUserErr?.response?.status, subUserErr?.response?.data || subUserErr.message);
      setError('Login failed. Please check your details and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateRandomPassword = () => {
    return Math.random().toString(36).slice(-10) + '!Ab1';
  };

  if (pageLoading) {
    return <LottieLoader message="Loading Groovesheets" />;
  }

  if (isLoading) {
    return (
      <div className="login-loading-screen">
        <div className="login-loading-box">
          <div className="login-loading-spinner"></div>
          <div className="login-loading-title">Logging in</div>
          <div className="login-loading-subtitle">Checking your account...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page-wrapper">
      <div className="login-container">
        <img src={HeaderImage} alt="Header" className="login-header-image" />
        <h2>Login</h2>
        {error && <p className="error">{error}</p>}

        <form onSubmit={handleLogin}>
          <div>
            <label>Email</label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" disabled={isLoading}>
            {isLoading ? 'Logging In...' : 'Login'}
          </button>
        </form>

        <div style={{ marginTop: '15px', textAlign: 'center' }}>
          <a href="https://edugroove.com/forgot_password" style={{ display: 'block', marginBottom: '10px' }}>
            Forgotten password?
          </a>
          <a href="https://edugroove.com/register/">
            Not a member? Register here
          </a>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
